import type { BatchHeader } from '~/features/catalog-operations/types/catalog-operations.types';

/** What a run was configured with, in one shape for the side panel's Run setup section and the Batch Info
 *  sheet — so the two can never disagree about a run. Read from the batch header's echoed
 *  run config; nothing here is derived from client state. */
export interface RunSetup {
  /** Requested attribute wire keys (e.g. `title`, `product_highlight`). */
  attributes: string[];
  /** Every language delivered, `en-US` first. `['en-US']` for an English-only run. */
  locales: string[];
  /** google-agent retailer prompt slug; `null` = the shared prompt. */
  retailer: string | null;
  searchDescriptors: boolean;
  phraseDescriptions: boolean;
  preScoring: boolean;
  postScoring: boolean;
}

export const SOURCE_LOCALE = 'en-US';

export function runSetupOf(header: BatchHeader): RunSetup {
  const cfg = header.runConfig;
  return {
    attributes: cfg?.contentAttributes ?? [],
    locales: header.locales.length > 0 ? header.locales : [SOURCE_LOCALE],
    retailer: cfg?.retailer ?? null,
    searchDescriptors: cfg?.searchDescriptors?.enabled ?? false,
    phraseDescriptions: cfg?.searchDescriptors?.phraseDescriptions ?? false,
    // No explicit scoring choice means the worker default, which runs — so `null` reads as On.
    preScoring: cfg?.scoring?.preScoring ?? true,
    postScoring: cfg?.scoring?.postScoring ?? true,
  };
}

/** `product_highlight` → `Product highlight`. Display only; the wire key stays the identity. */
export function humanizeAttribute(key: string): string {
  const words = key.replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** A display group of requested attributes. `labels` drop the group's own prefix
 *  (`shipping_weight` → `Weight` under Shipping). */
export interface AttributeGroup {
  group: 'copy' | 'classification' | 'dimensions' | 'shipping' | 'other';
  keys: string[];
  labels: string[];
}

const COPY = new Set([
  'title',
  'structured_title',
  'short_title',
  'description',
  'structured_description',
  'product_highlight',
  'product_detail',
]);
const CLASSIFICATION = new Set([
  'product_type',
  'google_product_category',
  'color',
  'material',
  'pattern',
  'size',
  'gender',
  'age_group',
]);

/** Requested attributes grouped for reading, in a fixed group order, each group in the
 *  run's own order. Empty groups are dropped. */
export function groupAttributes(keys: string[]): AttributeGroup[] {
  const buckets: Record<AttributeGroup['group'], string[]> = {
    copy: [],
    classification: [],
    dimensions: [],
    shipping: [],
    other: [],
  };
  for (const key of keys) {
    if (COPY.has(key)) buckets.copy.push(key);
    else if (CLASSIFICATION.has(key)) buckets.classification.push(key);
    else if (key.startsWith('shipping_')) buckets.shipping.push(key);
    else if (key.startsWith('product_') && /_(length|width|height|weight)$/.test(key))
      buckets.dimensions.push(key);
    else buckets.other.push(key);
  }
  const strip = (key: string, prefix: string) =>
    humanizeAttribute(key.startsWith(prefix) ? key.slice(prefix.length) : key);
  return (Object.keys(buckets) as AttributeGroup['group'][])
    .filter((g) => buckets[g].length > 0)
    .map((g) => ({
      group: g,
      keys: buckets[g],
      labels: buckets[g].map((k) =>
        g === 'shipping'
          ? strip(k, 'shipping_')
          : g === 'dimensions'
            ? strip(k, 'product_')
            : humanizeAttribute(k),
      ),
    }));
}
